/**
 * The game shell: owns the simulation, the renderer, the HUD, audio and input, and runs
 * the fixed-timestep loop that joins them.
 *
 * Ordering per frame is deliberate and load-bearing:
 *   1. consume input into simulation commands
 *   2. tick the simulation in exact 1/60 s steps
 *   3. sync presentation from the resulting state and drain events
 *   4. draw the HUD from a snapshot
 * Nothing downstream ever writes back upstream, which is what keeps a headless run and a
 * played run identical.
 */

import { Vector3, Matrix } from '@babylonjs/core/Maths/math.vector';
import { Ray } from '@babylonjs/core/Culling/ray';
import '@babylonjs/core/Culling/ray';
import { Viewport } from '@babylonjs/core/Maths/math.viewport';

import { RenderWorld, type QualityTier } from '../render/world';
import { SimRenderSync } from '../render/sync';
import { SimWorld } from '../sim/world';
import { AudioEngine } from '../audio/audioEngine';
import { Haptics } from './haptics';
import { Hud, type ViewMode } from '../ui/hud';
import { Screens } from '../ui/screens';
import { LookControls, zoomGestureMultiplier, mouseTriggerArmed } from '../input/lookControls';
import { shiftByIndex, SHIFT_COUNT, isEndless, endlessWatch } from '../content/shifts';
import { commsUrlsForScript } from '../content/radio';
import { WIDE_FOV_DEG, scopeFovDeg } from '../render/constants';
import {
  loadSave, writeSave, loadSettings, writeSettings, type SaveGame, type Settings, freshSave,
} from './save';
import { Tutorial } from '../ui/tutorial';
import { MANIFEST_ITEMS, upgradeById, type UpgradeId } from '../content/economy';
import type { RequisitionView } from '../ui/screens';
import { spawnAtBearing, stepVesselMotion } from '../sim/ships';
import { Rng } from '../sim/rng';
import type { WeaponId, EntityId, VesselKind } from '../sim/types';
import { vesselSpec } from '../content/vessels';
import { clamp } from '../sim/math';
import { DEPTH_CHARGE, PROJECTOR_MOUNT } from '../content/weapons';
import { daylightFactor } from '../sim/weather';
import { createGameTelemetry, type GameTelemetry } from './telemetry';

export const FIXED_DT = 1 / 60;
const MAX_FRAME_TIME = 0.25;

export interface GameOptions {
  canvas: HTMLCanvasElement;
  quality?: QualityTier;
  /** Skip the title screen and drop straight into a shift — used by benchmarks and tests. */
  autoStart?: boolean;
}

export class Game {
  readonly world: RenderWorld;
  readonly hud: Hud;
  readonly screens: Screens;
  readonly audio = new AudioEngine();
  readonly haptics = new Haptics();

  sim: SimWorld;
  private sync: SimRenderSync;
  private look: LookControls;

  private accumulator = 0;
  private lastNow = 0;
  private running = false;
  private paused = false;

  view: ViewMode = 'horizon';
  magnification = 2;

  /** The camera's forward ray, cached each frame for the sim aim and the scope HUD. */
  private readonly aimForward = { x: 0, y: 0, z: 1 };

  /**
   * Which inputs are currently holding the trigger down.
   *
   * A set rather than a boolean because on touch the trigger and the aim are two
   * different fingers, and the gun must not care which order they come and go in. The
   * on-screen fire button holds `'button'`; a captured mouse holds its pointer id.
   */
  private readonly fireHolds = new Set<number | 'button' | 'key'>();

  /** Where the player is looking, in degrees. Read by the debug surface and tests. */
  get cameraYawDeg(): number {
    return this.look.orientation.yaw;
  }

  get cameraPitchDeg(): number {
    return this.look.orientation.pitch;
  }

  /** Vertical aim inversion in force for the active view. */
  get inverted(): boolean {
    return this.look.invertPitch;
  }

  /**
   * Whether to present the game for a finger: the trigger button, and which control
   * routes the tutorial names.
   *
   * Seeded from the device rather than waiting for a touch to land on the canvas. It used
   * to start false and flip only when the player dragged the sea, so a phone that reached
   * the scope by pressing buttons was still being told to hold the left mouse button and
   * was not shown the trigger it needs to fire at all. A real touch still sets it, which
   * covers a hybrid device whose primary pointer is a mouse.
   */
  private touchMode = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  private spotlightOn = false;
  /** False while a tool or test is holding the beam on a fixed bearing. */
  private spotlightTracksLook = true;
  private pointerLocked = false;
  /**
   * When the pointer lock was last released, in milliseconds.
   *
   * Escape both exits the lock (browser behaviour, not ours) and is our pause key. Without
   * this, freeing the cursor to reach the HUD would always pause the game as a side
   * effect, which is not what the player asked for.
   */
  private lastUnlockAt = -Infinity;

  private save: SaveGame;
  private settings: Settings;
  private readonly tutorial: Tutorial;
  private readonly maxWideFov: number;
  private readonly telemetry: GameTelemetry;

  constructor(private readonly opts: GameOptions) {
    this.world = new RenderWorld({
      canvas: opts.canvas,
      ...(opts.quality ? { quality: opts.quality } : {}),
    });

    this.save = loadSave() ?? freshSave();
    this.settings = loadSettings();
    this.audio.setVolume(this.settings.volume);
    this.audio.setMuted(this.settings.muted);

    // The loadout matters only in Endless, where it stops a generated watch fielding a
    // hull the player has no answer to. See `substituteUnanswerable`.
    const openingScript = shiftByIndex(this.save.campaign.shift, this.save.campaign.resources);
    this.sim = new SimWorld({
      script: openingScript,
      resources: { ...this.save.campaign.resources },
      upgrades: this.save.campaign.upgrades,
    });
    // Speech for this shift, fetched alongside everything else. Never awaited: a line
    // that has not arrived is a line that does not play, and the text is on screen anyway.
    void this.audio.loadComms(commsUrlsForScript(openingScript));
    this.telemetry = createGameTelemetry();

    this.sync = new SimRenderSync({ world: this.world, sim: this.sim, audio: this.audio });
    this.sync.haptics = this.haptics;

    const portrait = window.innerHeight > window.innerWidth;
    this.maxWideFov = ((portrait ? 70 : WIDE_FOV_DEG) * Math.PI) / 180;
    this.world.camera.fov = this.maxWideFov;

    // Touch devices keep drag-look; a mouse gets pointer lock so aiming and firing stop
    // fighting each other over the same button.
    const pointerCoarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;
    this.look = new LookControls({
      camera: this.world.camera,
      element: opts.canvas,
      // Look rate is quoted at the wide view and scales down with the optic's field of
      // view from there, so 8× aims eight times finer rather than eight times faster.
      referenceFov: this.maxWideFov,
      pointerLock: !pointerCoarse,
      onPointerLockChange: (locked) => {
        this.pointerLocked = locked;
        if (!locked) {
          this.releaseAllFire();
          this.lastUnlockAt = performance.now();
        }
      },
      onZoom: (factor) => {
        if (this.view !== 'scope') return;
        this.setMagnification(
          this.magnification * zoomGestureMultiplier(factor, pointerCoarse),
        );
      },
    });
    this.look.setOrientation(portrait ? -7 : -13, portrait ? 13 : 8);

    this.hud = new Hud(document.body, {
      onSelectContact: (id) => this.sim.select(id),
      onSetView: (v) => this.setView(v),
      onSetWeapon: (w) => this.setWeapon(w),
      // The on-screen trigger is its own claim, independent of any aiming finger.
      onFireDown: () => this.setFireHold('button', true),
      onFireUp: () => this.setFireHold('button', false),
      onContactAcquired: (hostile: boolean) => this.audio.contactPing(hostile),
      onCycleFuse: () => this.cycleFuse(),
      onSetFuse: (depth) => this.sim.setFuseDepth(depth),
      onToggleSpotlight: () => this.toggleSpotlight(),
      onHorn: () => this.audio.klaxon(),
      onToggleInvert: () => this.toggleInvert(),
      onMenu: () => {
        // Release the optic's grip on the mouse first, or the pause screen appears
        // behind a captured cursor that cannot click any of it.
        if (this.pointerLocked) {
          try { document.exitPointerLock?.(); } catch { /* nothing locked */ }
        }
        this.setPaused(true);
      },
      onZoom: (m) => this.setMagnification(m),
      onCheatGesture: () => this.openShiftSkip(),
    });

    this.screens = new Screens(document.body, {
      onNewCampaign: () => this.startCampaign(1),
      onContinue: () => this.startCampaign(this.save.campaign.shift),
      onNextShift: () => this.beginNextShift(),
      onEndless: () => this.startEndless(),
      onRetryShift: () => this.startCampaign(this.save.campaign.shift),
      onResume: () => this.setPaused(false),
      onAbandon: () => {
        this.telemetry.abandonShift(this.sim.shift);
        this.showTitle();
      },
      onRequisition: () => this.showRequisition(),
      onBuyUpgrade: (id) => this.buyUpgrade(id),
      onOrderManifest: (id, delta) => this.orderManifest(id, delta),
      onSetVolume: (v) => {
        this.settings.volume = v;
        this.audio.setVolume(v);
        writeSettings(this.settings);
      },
      onSetMuted: (muted) => {
        this.settings.muted = muted;
        this.audio.setMuted(muted);
        writeSettings(this.settings);
      },
      onSetInvert: (view, inverted) => {
        if (view === 'scope') this.settings.invertScope = inverted;
        else this.settings.invertLook = inverted;
        this.applyInvert();
        writeSettings(this.settings);
      },
    });

    // The screen rebuilds itself from live campaign state after every purchase.
    this.screens.requisitionView = () => this.requisitionView();

    // Shift 1 doubles as the tutorial (plan A7): a prompt layer over the real shift,
    // never a separate level.
    this.tutorial = new Tutorial(document.body, () => { /* skipped; nothing to unwind */ });

    this.attachInput();
    window.addEventListener('pagehide', () => this.telemetry.leaveShift(this.sim.shift));

    if (opts.autoStart) {
      this.startCampaign(this.save.campaign.shift);
    } else {
      /*
       * The enter gate, first load only. It is rendered here, underneath the boot
       * overlay, so it is revealed at the moment loading finishes rather than competing
       * with it. Returning to the title later goes straight to the menu — the gate's
       * whole job is the one gesture that lets the intro make noise.
       */
      this.screens.showEnter(() => {
        this.audio.unlock();
        this.showTitle();
      });
    }
  }

  // --- Lifecycle ----------------------------------------------------------------------

  /**
   * The no-dead-ends guarantee (plan A5).
   *
   * Campaign-critical equipment travels as scripted deliveries, which means it can be
   * sunk — and a player who lost the launcher two shifts ago must not arrive at the
   * Kraken holding a gun that cannot hurt it. Before the finale the rig is topped up to a
   * minimum boss loadout, free, no questions. It is a floor rather than a grant: a player
   * who has been careful gets nothing and does not need it.
   *
   * Deliberately silent when nothing is added. A message reading "we have given you the
   * things you already had" is worse than no message.
   */
  private emergencyResupply(shiftIndex: number): void {
    if (shiftIndex < SHIFT_COUNT) return;
    const r = this.save.campaign.resources;
    const before = { t: r.torpedoes, d: r.depthCharges, a: r.ammoReserve };
    r.torpedoes = Math.max(r.torpedoes, 4);
    r.depthCharges = Math.max(r.depthCharges, 8);
    r.ammoReserve = Math.max(r.ammoReserve, 1200);
    if (r.torpedoes !== before.t || r.depthCharges !== before.d || r.ammoReserve !== before.a) {
      console.warn('[deepwater] emergency resupply applied for the finale');
    }
  }

  /**
   * The shift skipper — Adam's, for testing the deployed build (see `wireCheatGesture`).
   *
   * Not gated on a build flag. It ships to production on purpose: production is the thing
   * he needs to test, on a phone, where there is no console and the only other route to a
   * late shift is playing eleven of them. The gesture that opens it is the gate.
   */
  private openShiftSkip(): void {
    this.paused = true;
    this.releaseAllFire();
    this.hud.hide();
    this.screens.showShiftSkip(
      this.save.campaign.shift,
      (index) => this.skipToShift(index),
      () => this.setPaused(false),
    );
  }

  /**
   * Start a shift outright, kitted for it.
   *
   * A floor rather than a grant, like `emergencyResupply`: a player who arrives here well
   * stocked keeps what they had. Jumping to shift nine with shift one's magazine would
   * only test how fast the rig dies, which is not what the menu is for.
   */
  private skipToShift(index: number): void {
    const r = this.save.campaign.resources;
    r.ammoReserve = Math.max(r.ammoReserve, 6000);
    r.torpedoes = Math.max(r.torpedoes, 8);
    r.depthCharges = Math.max(r.depthCharges, 12);
    r.money = Math.max(r.money, 500_000);
    r.integrity = 100;
    r.crew = r.crewCapacity;
    // Endless is reachable from here whether or not the campaign has been finished, which
    // is the point of a test menu; unlock it so the title screen agrees afterwards.
    if (isEndless(index)) this.save.campaign.endlessUnlocked = true;
    this.startCampaign(index);
  }

  /** Begin, or continue, the Endless watch. */
  private startEndless(): void {
    const next = isEndless(this.save.campaign.shift)
      ? endlessWatch(this.save.campaign.shift) + 1
      : 1;
    this.startCampaign(100 + next);
  }

  showTitle(): void {
    this.paused = true;
    this.hud.hide();
    this.tutorial.stop();
    // Leaving mid-shift takes the channel with you; nothing said out there follows the
    // player to the menu.
    this.audio.clearComms();
    this.screens.showTitle(this.save.campaign.shift > 1, this.save.campaign.shift, {
      unlocked: this.save.campaign.endlessUnlocked,
      best: this.save.campaign.endlessBest,
    });
  }

  startCampaign(shiftIndex: number): void {
    this.save.campaign.shift = shiftIndex;
    this.emergencyResupply(shiftIndex);

    const script = shiftByIndex(shiftIndex, this.save.campaign.resources);
    this.sim = new SimWorld({
      script,
      resources: { ...this.save.campaign.resources },
      upgrades: this.save.campaign.upgrades,
      pendingManifest: this.save.campaign.pendingManifest,
    });
    this.audio.clearComms();
    void this.audio.loadComms(commsUrlsForScript(script));
    /*
     * The order stays on the books until a hull actually loads it — see `endShift`, which
     * is the only place it is cleared. Clearing it here, at the moment the shift opened,
     * was what let a shift with no supply run in it (10 and 12, the finale among them)
     * take the money and deliver nothing.
     */
    // The outgoing sync owns scene objects — the weapon mounts among them — and the new
    // one builds its own. Without this the old gun is orphaned in the scene.
    this.sync.dispose();
    this.sync = new SimRenderSync({ world: this.world, sim: this.sim, audio: this.audio });
    this.sync.haptics = this.haptics;

    // The very first interaction with the page is what unlocks audio on every mobile
    // browser; starting a shift is always downstream of a click, so this is the spot.
    this.audio.unlock();
    this.audio.setAmbience(this.sim.weather.seaState, this.sim.weather.windSpeed);

    this.setView('horizon');
    this.screens.hide();
    this.hud.show();
    // The message buffer is rebuilt with the sync; the log on screen has to go with it.
    this.hud.resetRadio();
    this.paused = false;

    if (shiftIndex === 1) {
      this.tutorial.start({
        snapshot: this.sim.snapshot(),
        view: this.view,
        cameraYaw: this.world.camera.rotation.y,
        touch: this.touchMode,
      });
    } else {
      this.tutorial.stop();
    }

    this.telemetry.startShift(shiftIndex, this.world.qualityTier, this.sim.shift);
    writeSave(this.save);
  }

  // --- Requisition ----------------------------------------------------------------------

  private requisitionView(): RequisitionView {
    return {
      resources: this.save.campaign.resources,
      upgrades: this.save.campaign.upgrades,
      pendingManifest: this.save.campaign.pendingManifest,
      nextShift: Math.min(SHIFT_COUNT, this.save.campaign.shift + 1),
    };
  }

  showRequisition(): void {
    this.paused = true;
    this.hud.hide();
    this.screens.showRequisition(this.requisitionView());
  }

  /**
   * Leave the requisition screen for the next shift, teaching the manifest on the way out.
   *
   * The notice only stands between shift 1 and shift 2, and only for a player who has
   * actually ordered something — see `Screens.showManifestNotice`. Everything else about
   * starting a shift is unchanged: dismissing it lands in exactly the call the button used
   * to make.
   */
  private beginNextShift(): void {
    const next = Math.min(SHIFT_COUNT, this.save.campaign.shift + 1);
    const firstOrder = this.save.campaign.shift === 1
      && this.save.campaign.pendingManifest.length > 0;

    if (firstOrder) {
      this.screens.showManifestNotice(next, () => this.startCampaign(next));
      return;
    }
    this.startCampaign(next);
  }

  /** Instant, permanent, and capacity-free — upgrades are fitted to the rig itself. */
  private buyUpgrade(id: UpgradeId): boolean {
    const upgrade = upgradeById(id);
    const campaign = this.save.campaign;
    if (!upgrade) return false;
    if (campaign.upgrades.includes(id)) return false;
    if (campaign.resources.money < upgrade.cost) return false;

    campaign.resources.money -= upgrade.cost;
    campaign.upgrades.push(id);

    // Capacity effects apply the moment they are fitted, not at the next shift.
    if (id === 'storageTanks') campaign.resources.oilCapacity += 6000;
    /*
     * The extra tube pair needs nothing here. It used to run `torpedoes += 0` — a
     * literal no-op, so the upgrade did nothing at all — because it is not a stock of
     * torpedoes but a property of the rig: the sim reads it when it builds the shift's
     * tube capacity.
     */

    writeSave(this.save);
    return true;
  }

  /**
   * Order or cancel a manifest item.
   *
   * Money moves at order time, and cancelling refunds in full — the goods have not been
   * loaded yet. Once the shift starts they become cargo and the refund stops being
   * available, which is exactly the point at which they become worth defending.
   */
  private orderManifest(id: string, delta: number): number {
    const item = MANIFEST_ITEMS.find((m) => m.id === id);
    const campaign = this.save.campaign;
    if (!item) return 0;

    if (delta > 0) {
      if (campaign.resources.money < item.cost) return this.manifestCount(id);
      campaign.resources.money -= item.cost;
      campaign.pendingManifest.push(id);
    } else if (delta < 0) {
      const idx = campaign.pendingManifest.indexOf(id);
      if (idx >= 0) {
        campaign.pendingManifest.splice(idx, 1);
        campaign.resources.money += item.cost;
      }
    }

    writeSave(this.save);
    return this.manifestCount(id);
  }

  private manifestCount(id: string): number {
    return this.save.campaign.pendingManifest.filter((x) => x === id).length;
  }

  private endShift(outcome: 'complete' | 'defeat'): void {
    this.paused = true;
    this.releaseAllFire();
    this.audio.stopGunVoices();
    // A torpedo still running when the shift ends must not keep whining over the report.
    this.sync.stopWeaponVoices();
    /*
     * Neither must the operator. A won shift seals the channel itself with its sign-off
     * (`Radio.final`), so this catches the endings that arrive without one — defeat and
     * the watchdog — where a backlog raised in the last seconds of the fight would
     * otherwise carry on talking over the results.
     */
    this.audio.sealComms();
    this.hud.hide();
    this.tutorial.stop();
    this.telemetry.endShift(outcome, this.sim.shift);

    if (outcome === 'complete') {
      // Bank the shift's results into the campaign before showing the report.
      this.save.campaign.resources = { ...this.sim.resources };
      /*
       * Equipment that arrived by ship this watch. Cargo could only ever move numbers
       * before, so hardware the script promised over the radio was never actually fitted
       * — the hydrophone was announced on the shift built around needing it and then
       * only obtainable for $170,000 in the shop.
       */
      for (const id of this.sim.fittedUpgrades) {
        if (!this.save.campaign.upgrades.includes(id)) {
          this.save.campaign.upgrades.push(id);
        }
      }
      /*
       * An order is spent once a hull has carried it, however that hull ended up. One that
       * never found a vessel to ride on is still owed, and travels to the next shift that
       * has one rather than being written off against a supply run that was never staged.
       */
      if (this.sim.manifestDispatched) this.save.campaign.pendingManifest = [];
      this.save.campaign.stats.shiftsCompleted++;
      this.save.campaign.stats.moneyEarned += this.sim.shift.earned;
      this.save.campaign.stats.hostilesDestroyed += this.sim.shift.hostilesDestroyed;
      /*
       * Finishing shift 12 is the end of the campaign, not another shift report. The
       * victory screen is the only place Endless can be unlocked from, which is what
       * makes it a reward rather than a menu option.
       */
      if (this.save.campaign.shift === SHIFT_COUNT) {
        this.save.campaign.endlessUnlocked = true;
        writeSave(this.save);
        this.screens.showVictory(this.sim.resources, this.save.campaign.stats);
        return;
      }

      // An Endless watch simply rolls into the next one.
      if (isEndless(this.save.campaign.shift)) {
        const watch = endlessWatch(this.save.campaign.shift);
        this.save.campaign.endlessBest = Math.max(this.save.campaign.endlessBest ?? 0, watch);
        writeSave(this.save);
        this.screens.showShiftReport(this.sim.shift, this.sim.resources, false);
        return;
      }

      const last = this.save.campaign.shift >= SHIFT_COUNT;
      writeSave(this.save);
      this.screens.showShiftReport(this.sim.shift, this.sim.resources, last);
    } else {
      this.screens.showDefeat(this.sim.shift);
    }
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastNow = performance.now();

    this.world.engine.runRenderLoop(() => this.frame());
  }

  /**
   * One frame, guarded.
   *
   * An exception thrown inside a render-loop callback silently stops Babylon calling it
   * again: the canvas keeps showing the last frame and the game looks merely frozen. That
   * cost real debugging time once already, so a failure is logged loudly and the loop
   * survives — the console-error gate in the e2e suite still fails the build.
   */
  private frame(): void {
    try {
      this.frameInner();
    } catch (err) {
      if (!this.loopErrorReported) {
        this.loopErrorReported = true;
        console.error('[deepwater] frame failed; loop continues', err);
      }
    }
  }

  private loopErrorReported = false;

  private frameInner(): void {
    const now = performance.now();
    let frameTime = (now - this.lastNow) / 1000;
    this.lastNow = now;
    if (frameTime > MAX_FRAME_TIME) frameTime = MAX_FRAME_TIME;

    if (!this.paused) {
      this.pushAim();

      this.accumulator += frameTime;
      let steps = 0;
      while (this.accumulator >= FIXED_DT && steps < 8) {
        this.sim.tick(FIXED_DT);
        this.accumulator -= FIXED_DT;
        steps++;
      }

      this.sync.update(frameTime);
      this.audio.update();

      if (this.tutorial.isActive) {
        this.tutorial.update({
          snapshot: this.sim.snapshot(),
          view: this.view,
          cameraYaw: this.world.camera.rotation.y,
          touch: this.touchMode,
        }, frameTime);
      }

      if (this.sim.shift.outcome === 'running') {
        this.telemetry.tick(this.sim.shift);
      } else {
        this.endShift(this.sim.shift.outcome);
      }
    }

    this.world.render();

    if (!this.paused) this.drawHud(frameTime);
  }

  // --- Input --------------------------------------------------------------------------

  /**
   * The gun points where the camera points.
   *
   * In the wide view that is the centre of the screen; in the scope it is the reticle,
   * which is the same ray. What is *not* the same is the origin: shells leave the gun
   * mount, not the eye, and the plan requires that parallax be honest rather than hidden.
   */
  private pushAim(): void {
    const m = this.world.camera.getWorldMatrix();
    const forward = Vector3.TransformNormal(new Vector3(0, 0, 1), m).normalize();
    this.aimForward.x = forward.x;
    this.aimForward.y = forward.y;
    this.aimForward.z = forward.z;
    this.sim.setAim({ x: forward.x, y: forward.y, z: forward.z });
  }

  /**
   * Where the projector's lob will land: the aim ray from the *mount* intersected with
   * the sea, exactly as `lobDepthCharge` computes it. The scope's impact marker draws
   * this point, and it is visibly not the crosshair centre — the eye is 20 m above the
   * mount and 35 m north of it, and the marker is the honest rendering of that parallax
   * rather than a decoration pinned to the reticle.
   */
  private projectorAimPoint(): { x: number; z: number } | null {
    const d = this.aimForward;
    if (d.y >= -1e-3) return null;
    const t = PROJECTOR_MOUNT.y / -d.y;
    return { x: PROJECTOR_MOUNT.x + d.x * t, z: PROJECTOR_MOUNT.z + d.z * t };
  }

  /** Add or drop one input's claim on the trigger; the gun fires while any claim holds. */
  private setFireHold(source: number | 'button' | 'key', on: boolean): void {
    if (on) this.fireHolds.add(source);
    else this.fireHolds.delete(source);
    this.sim.setFiring(this.fireHolds.size > 0);
  }

  /** Drop every claim — for pausing, backgrounding, or losing the window. */
  private releaseAllFire(): void {
    this.fireHolds.clear();
    this.sim.setFiring(false);
  }

  private attachInput(): void {
    const canvas = this.opts.canvas;

    canvas.addEventListener('pointerdown', (e) => {
      if (this.paused) return;
      // A touch promotes to touch mode; a mouse click does not demote a coarse device,
      // or a stylus tap on a tablet would take the trigger away mid-shift.
      if (e.pointerType === 'touch') this.touchMode = true;
      if (e.pointerType !== 'mouse' || e.button !== 0) return;

      /*
       * The trigger is armed by `mouseTriggerArmed`, not by the lock alone.
       *
       * It used to read `if (!this.pointerLocked) return`, which quietly made the pointer
       * lock a hard requirement for firing at all — and WebKit refuses the lock outright,
       * answering every request with `pointerlockerror`. Desktop Safari could therefore
       * aim the scope by dragging and never fire, because the on-screen trigger is
       * coarse-pointer only and the left button was the only other way in.
       *
       * Note this must stay on Pointer Events. The obvious tidier fix — put the trigger
       * on `mousedown` next to the look's `mousemove` — cannot work: Babylon calls
       * `preventDefault` on the canvas `pointerdown`, which suppresses the compatibility
       * mouse events, and `mousedown` never fires on the canvas at all. Measured in both
       * engines before believing it.
       */
      if (mouseTriggerArmed(this.view === 'scope', this.pointerLocked, this.look.lockUnavailable)) {
        // Scope only, by Adam's rule — see `mouseTriggerArmed`. HORIZON stays a survey
        // view where the button just looks, because dragging to look IS a held left
        // button and arming it there emptied the magazine on every sweep; `Space` is the
        // wide view's trigger. Down the optic it fires — and where the lock was refused
        // the same press also drags the view, which is the whole point.
        this.setFireHold(e.pointerId, true);
        /*
         * Keep asking, even while firing.
         *
         * Chromium raises `pointerlockerror` for a request made too soon after the last
         * exit. Without this the trigger would arm on that one transient refusal and then
         * never ask again — because the armed branch returns — stranding a browser that
         * works perfectly well in the fallback for the rest of the session. Once a lock is
         * granted `lockUnavailable` goes false for good and this stops being reachable.
         */
        if (!this.pointerLocked && this.view === 'scope') this.look.requestLock();
        return;
      }

      // First click captures the pointer; it does not fire. After that the mouse aims
      // freely and the left button means exactly one thing.
      // In the scope with the cursor free — the player released it to reach the HUD —
      // clicking the sea takes it back. Clicking a HUD control does not reach here,
      // because those are DOM elements above the canvas.
      if (this.view === 'scope') this.look.requestLock();
    });

    /*
     * A release only stops the gun if it is the release of the finger that started it.
     *
     * This was a plain `setFiring(false)` on a `window` pointerup, so *any* pointer
     * lifting anywhere stopped the gun — including the thumb that had just finished
     * swiping the view around. On a phone that made the two things a gunner does most
     * mutually exclusive: hold the trigger, drag to track, and the burst died the moment
     * the tracking thumb came off the glass. It cost nothing on desktop, where firing and
     * looking are the same captured pointer, which is why it survived this long.
     */
    const stopFiring = (ev: PointerEvent): void => this.setFireHold(ev.pointerId, false);
    canvas.addEventListener('pointerup', stopFiring);
    canvas.addEventListener('pointercancel', stopFiring);
    canvas.addEventListener('lostpointercapture', stopFiring);
    window.addEventListener('pointerup', stopFiring);
    // Losing the window is not one input releasing, it is every input going away.
    window.addEventListener('blur', () => this.releaseAllFire());

    // Backgrounding pauses rather than fast-forwarding: the plan forbids a bulk catch-up
    // simulation on return, and iOS kills audio contexts here too.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.releaseAllFire();
        this.setPaused(true, true);
      } else {
        this.audio.resume();
      }
    });

    window.addEventListener('keyup', (e) => {
      if (e.code === 'Space') this.setFireHold('key', false);
    });

    window.addEventListener('keydown', (e) => {
      if (e.repeat) return;
      this.pushCheatKey(e.key);
      switch (e.code) {
        case 'Tab':
          e.preventDefault();
          this.setView(this.view === 'horizon' ? 'scope' : 'horizon');
          break;
        /*
         * Space is the trigger, in both views (Adam, 2026-07-30).
         *
         * In HORIZON it is the ONLY desktop trigger, and that is deliberate: the mouse
         * arms down the optic alone, because out here dragging to look is itself a held
         * left button, so a mouse trigger fired on every survey sweep. A key separates
         * the two without taking the mouse away from looking around, which is what the
         * wide view is for. Keep this reachable in both views or HORIZON loses its gun —
         * it is the whole reason `mouseTriggerArmed` can afford to refuse there.
         *
         * It used to mean "enter the scope on the selected contact". That went with the
         * snap it depended on: the optic no longer jumps to a contact, so the key was
         * left doing nothing TAB did not already do.
         */
        case 'Space':
          e.preventDefault();
          this.setFireHold('key', true);
          break;
        case 'KeyQ': this.sim.cycleSelection(-1); break;
        case 'KeyE': this.sim.cycleSelection(1); break;
        case 'Digit1': this.setWeapon('autocannon'); break;
        case 'Digit2': this.setWeapon('torpedo'); break;
        case 'Digit3': this.setWeapon('depthCharge'); break;
        case 'KeyL': this.toggleSpotlight(); break;
        case 'KeyF': this.cycleFuse(); break;
        case 'KeyW': this.toggleInvert(); break;
        case 'KeyC': this.releaseCursor(); break;
        case 'KeyH': this.audio.klaxon(); break;
        // Zoom without the wheel, so the scope is fully drivable from the keyboard.
        case 'Minus':
        case 'NumpadSubtract':
          if (this.view === 'scope') this.setMagnification(this.magnification - 1);
          break;
        case 'Equal':
        case 'NumpadAdd':
          if (this.view === 'scope') this.setMagnification(this.magnification + 1);
          break;
        case 'Escape': {
          // The browser consumes Escape to release the pointer lock. Treat that press as
          // "give me my cursor back" rather than "pause": a second Escape pauses.
          const justUnlocked = performance.now() - this.lastUnlockAt < 400;
          if (this.pointerLocked || justUnlocked) break;
          if (!this.screens.isShowing) this.setPaused(!this.paused);
          break;
        }
        default: break;
      }
    });

    window.addEventListener('resize', () => {
      this.world.resize();
      this.hud.resize();
    });
    window.addEventListener('orientationchange', () => {
      window.setTimeout(() => { this.world.resize(); this.hud.resize(); }, 120);
    });
  }

  // --- View and weapon state ------------------------------------------------------------

  setView(view: ViewMode): void {
    this.view = view;
    // The gun only acquires down the optic — see `SimWorld.stepGunLock`.
    this.sim.setScoped(view === 'scope');

    // Putting your eye to the optic captures the mouse; stepping back releases it. This
    // is what keeps aiming and firing off the same button without making the HUD
    // unclickable in the view where all the clicking happens.
    if (view === 'scope') {
      this.look.requestLock();
    } else if (this.pointerLocked) {
      try { document.exitPointerLock?.(); } catch { /* nothing locked */ }
    }

    this.applyInvert();

    if (view === 'scope') {
      /*
       * The optic opens wherever the wide view was pointed. Always (Adam, 2026-07-30).
       *
       * It used to snap onto the selected contact, per the plan's "entering scope on a
       * selected contact aims at that contact". In practice that is the optic taking the
       * aim away from the player at the exact moment they went to the trouble of lining
       * something up by eye — and with a contact selected it fired on every ordinary TAB,
       * so the wide view's aim could not be carried into the scope at all. Both views
       * already share one camera, so leaving it alone is all this needs to do.
       */
      this.setMagnification(this.magnification);
    } else {
      this.world.camera.fov = this.maxWideFov;
    }
  }

  setMagnification(m: number): void {
    this.magnification = Math.max(2, Math.min(8, m));
    if (this.view === 'scope') {
      // Derived from the wide FOV so the number on the HUD is a real magnification.
      this.world.camera.fov = (scopeFovDeg(this.magnification) * Math.PI) / 180;
    }
  }

  private setWeapon(w: WeaponId): void {
    // Later weapons are gated by campaign progress; selecting one you do not have yet
    // should do nothing rather than silently arm an empty mount.
    if (w === 'torpedo' && this.sim.resources.torpedoes <= 0) return;
    if (w === 'depthCharge' && this.sim.resources.depthCharges <= 0) return;
    this.sim.setWeapon(w);
  }

  /**
   * Hand the cursor back so the HUD can be clicked.
   *
   * Escape does this too, but that is the browser releasing the lock rather than us, and
   * it cannot be exercised by an automated harness — synthetic key events do not trigger
   * it. Calling exitPointerLock ourselves is a route that always works and can be tested,
   * so the player is never left with only an untested way out of a captured pointer.
   * Clicking the sea takes the cursor back again.
   */
  releaseCursor(): void {
    if (!this.pointerLocked) return;
    try { document.exitPointerLock?.(); } catch { /* nothing locked */ }
  }

  /** Each view carries its own inversion, so switching views re-applies the right one. */
  private applyInvert(): void {
    this.look.invertPitch = this.view === 'scope'
      ? this.settings.invertScope
      : this.settings.invertLook;
  }

  /**
   * Flip vertical aim for the view the player is currently in.
   *
   * Reachable from the HUD button and the I key without pausing, because the moment you
   * find out the aim feels backwards is the moment you are trying to track something.
   */
  toggleInvert(): void {
    if (this.view === 'scope') {
      this.settings.invertScope = !this.settings.invertScope;
    } else {
      this.settings.invertLook = !this.settings.invertLook;
    }
    this.applyInvert();
    writeSettings(this.settings);
    this.hud.setInvertState(this.look.invertPitch, this.view === 'scope');
  }

  /**
   * Step the depth-charge fuse through 10 / 25 / 45 m.
   *
   * On its own key rather than buried in the weapon card, because it is set *before* the
   * shot and the shot is time-critical: a sub crossing at 25 m does not wait for a menu.
   */
  private cycleFuse(): void {
    const depths = DEPTH_CHARGE.fuseDepths;
    const current = depths.indexOf(this.sim.rig.fuseDepth);
    const next = depths[(current + 1) % depths.length] ?? depths[0] ?? 25;
    this.sim.setFuseDepth(next);
  }

  private toggleSpotlight(): void {
    this.spotlightOn = !this.spotlightOn;
    this.spotlightTracksLook = true;
    this.sim.spotlightBearing = this.spotlightOn ? this.look.orientation.yaw * (Math.PI / 180) : null;
    this.hud.setSpotlightState(this.spotlightOn);
  }

  /**
   * Point the searchlight at an explicit bearing and depression, bypassing the look.
   *
   * Exists so tests and tools can drive the beam without synthesising key presses and a
   * mouse look, which is how its total absence from the water went unnoticed: nothing
   * automated could turn it on, so nothing automated could see that it did nothing.
   */
  aimSpotlight(bearingDeg: number | null, depressionDeg = -7): void {
    this.spotlightOn = bearingDeg !== null;
    this.spotlightTracksLook = false;
    this.sim.spotlightBearing = bearingDeg === null ? null : bearingDeg * (Math.PI / 180);
    if (bearingDeg !== null) {
      this.sync.searchlightPitch = clamp(depressionDeg * (Math.PI / 180), -0.52, 0);
    }
    this.hud.setSpotlightState(this.spotlightOn);
    this.sync.update(0);
  }

  /**
   * Point the view, through the same look controls the mouse drives.
   *
   * Debug surface only. Exists because the scope's sight pictures depend on where the
   * crosshair sits — the projector's impact marker needs a downward aim before it draws
   * anything — and a headless test cannot synthesise pointer-locked mouse movement.
   */
  setLook(yawDeg: number, pitchDeg: number): void {
    this.look.setOrientation(yawDeg, pitchDeg);
  }

  setPaused(paused: boolean, silent = false): void {
    this.paused = paused;
    if (paused) {
      this.releaseAllFire();
      /*
       * Silence goes with the freeze.
       *
       * The sim already stopped stepping when paused, but the audio graph did not — so a
       * paused game went on playing the tail of a burst, the screw whine of a torpedo
       * still notionally running, and the full battle ambience. Suspending the context
       * freezes every voice at once, which is also the only way to catch the looping
       * ones without hunting them down individually.
       */
      this.audio.suspend();
      if (!silent) this.screens.showPause(this.settings);
    } else {
      this.screens.hide();
      this.hud.show();
      this.audio.resume();
      // Discard the wall time that elapsed while paused, so nothing catches up.
      this.lastNow = performance.now();
      this.accumulator = 0;
    }
  }

  // --- HUD ------------------------------------------------------------------------------

  private drawHud(dt: number): void {
    const snapshot = this.sim.snapshot();
    const engine = this.world.engine;
    const width = engine.getRenderWidth();
    const height = engine.getRenderHeight();
    const camera = this.world.camera;
    const viewProj = camera.getViewMatrix().multiply(camera.getProjectionMatrix());
    const identity = Matrix.Identity();

    /*
     * Track the spotlight to wherever the player is looking while it is on.
     *
     * The pitch is negated. Babylon's camera rotation is positive looking DOWN, while a
     * direction vector's Y is positive pointing UP, so feeding one straight into the
     * other inverts the beam against the player's aim.
     *
     * The depression is only floored, never capped short of the horizon. The beam is
     * allowed all the way up to level, where it simply reaches further and further out
     * and fades on its own — capping it a few degrees down, as an earlier attempt did to
     * guarantee the pool landed on water, took away exactly the long throw that makes a
     * searchlight feel like one.
     */
    if (this.spotlightOn && this.spotlightTracksLook) {
      const look = this.look.orientation;
      this.sim.spotlightBearing = look.yaw * (Math.PI / 180);
      this.sync.searchlightPitch = clamp(-look.pitch * (Math.PI / 180), -0.52, 0);
    }

    /*
     * The lead pip follows the crosshair, not the selection.
     *
     * It used to be drawn for whatever was selected, so a hull already centred in the
     * sight got no solution until the player had also tapped it — having aimed at the
     * thing, you had to say so a second time. The gun now acquires on dwell like the
     * torpedo does, and the selection follows the pip rather than gating it.
     */
    const locked = snapshot.gunLock.locked ? snapshot.gunLock.targetId : null;
    const target = locked !== null
      ? this.sim.vessels.find((v) => v.id === locked)
      : undefined;
    const solution = target ? this.sim.leadSolution(target) : null;

    this.hud.setInvertState(this.look.invertPitch, this.view === 'scope');
    this.hud.setTutorialActive(this.tutorial.isActive);

    this.hud.update({
      snapshot,
      view: this.view,
      magnification: this.magnification,
      cameraYaw: camera.rotation.y,
      messages: this.sync.messages,
      lockWarnAt: this.sync.lockWarnAt,
      hullHitAt: this.sync.hullHitAt,
      aimWater: this.projectorAimPoint(),
      nightFactor: 1 - daylightFactor(this.sim.weather.timeOfDay),
      touch: this.touchMode,
      inverted: this.look.invertPitch,
      pointerLocked: this.pointerLocked,
      lead: solution
        ? { x: solution.point.x, y: solution.point.y, z: solution.point.z, flightTime: solution.flightTime }
        : null,
      project: (x, y, z) => {
        const p = Vector3.Project(
          new Vector3(x, y, z),
          identity,
          viewProj,
          new Viewport(0, 0, width, height),
        );
        // Babylon projects points behind the camera to plausible-looking coordinates;
        // the depth check is what stops contacts astern appearing in front of you.
        const behind = Vector3.TransformCoordinates(
          new Vector3(x, y, z), camera.getViewMatrix(),
        ).z <= 0;
        const dpr = width / Math.max(1, window.innerWidth);
        return {
          x: p.x / dpr,
          y: p.y / dpr,
          visible: !behind && p.x >= -80 && p.y >= -80 && p.x <= width + 80 && p.y <= height + 80,
        };
      },
    }, dt);
  }

  // --- Debug surface (window.__dw) --------------------------------------------------------

  /** Drop a vessel into the running shift. Returns its entity id. */
  spawnVessel(kind: string, bearingDeg: number, rangeMetres: number): number {
    const vessel = spawnAtBearing(
      kind as VesselKind,
      bearingDeg,
      rangeMetres,
      kind === 'tanker' ? { oilCollection: true, label: 'Crude collection' } : undefined,
    );
    this.sim.vessels.push(vessel);
    return vessel.id;
  }

  /**
   * Push simulation state into the renderer once, without advancing anything.
   *
   * Benchmark runs sit paused so the clock stays pinned, which means the frame loop never
   * reaches the sync step and vessels would never receive their instances. This is the
   * one place that reconciliation is driven by hand.
   */
  syncForCapture(): void {
    this.sync.update(0);
  }

  /**
   * Place a vessel and settle it onto the wave field at an exact clock, for deterministic
   * captures. dt of zero moves nothing but samples buoyancy at `time`, so a benchmark
   * ship sits on precisely the water the frame will draw.
   */
  placeVesselForCapture(
    kind: string,
    bearingDeg: number,
    rangeKm: number,
    time: number,
    headingDeg?: number,
    submerged = false,
    sinkProgress?: number,
  ): void {
    const id = this.spawnVessel(kind, bearingDeg, rangeKm * 1000);
    const vessel = this.sim.vessels.find((v) => v.id === id);
    if (!vessel) return;
    if (headingDeg !== undefined) vessel.heading = (headingDeg * Math.PI) / 180;
    vessel.submerged = submerged;
    // A staged hull is already wherever it is, not on its way there: the dive ramp is a
    // running animation and a capture has no seconds to spend on it. Without this a
    // surfaced Kraken placed for a benchmark kept the dived value it was created with
    // and rendered three hundred metres under the sea.
    vessel.submergence = submerged ? 1 : 0;
    this.sim.time = time;
    vessel.detection = 'identified';
    if (sinkProgress !== undefined) {
      const spec = vesselSpec(vessel.kind);
      const progress = Math.min(0.99, Math.max(0, sinkProgress));
      vessel.state = 'sinking';
      vessel.stateTimer = spec.smokeDuration + spec.sinkDuration * progress;
      vessel.speed = 0;
    }
    stepVesselMotion(vessel, this.sim.waveField, time, 0, new Rng(`bench-${id}`), false);
  }

  /**
   * Sweep the free-look arc and report where rig structure blocks the line of sight.
   *
   * Rays are cast from the eye at a shallow depression — the angle a vessel a couple of
   * kilometres out actually sits at — and tested against the rig meshes directly rather
   * than through scene picking, which the rig opts out of.
   */
  maskedBearings(pitchDeg: number): Array<{ from: number; to: number }> {
    const origin = this.world.camera.position;
    const meshes = this.world.rigMeshes;
    const blocked: number[] = [];

    for (let deg = -110; deg <= 110; deg += 1) {
      const yaw = (deg * Math.PI) / 180;
      const pitch = (pitchDeg * Math.PI) / 180;
      const dir = new Vector3(
        Math.sin(yaw) * Math.cos(pitch),
        -Math.sin(pitch),
        Math.cos(yaw) * Math.cos(pitch),
      );
      const ray = new Ray(origin, dir, 4000);
      const hit = meshes.some((m) => ray.intersectsMesh(m).hit);
      if (hit) blocked.push(deg);
    }

    // Collapse the sampled degrees into contiguous sectors.
    const sectors: Array<{ from: number; to: number }> = [];
    for (const deg of blocked) {
      const last = sectors[sectors.length - 1];
      if (last && deg - last.to <= 2) last.to = deg;
      else sectors.push({ from: deg, to: deg });
    }
    return sectors;
  }

  /**
   * Adam's desktop cheats, in his words: iddqd for levels, idkfa for full ammo.
   *
   * The phone has the four-control hold (`wireCheatGesture`), which desktop cannot
   * perform — the on-screen trigger is coarse-pointer only and is never rendered for a
   * mouse. A typed code is the desktop equivalent and needs no chord, no modifier and no
   * button that has to exist.
   *
   * Letters are appended to a short rolling buffer and matched on its tail, so a mistyped
   * run does not have to be cleared before trying again. Only letters go in, which keeps
   * the buffer from filling with arrow keys and modifiers between attempts.
   *
   * The single-letter bindings still fire while a code is typed — the q in iddqd cycles
   * the selection on its way past. That is the same bargain the phone gesture makes, and
   * the alternative is swallowing keystrokes on the chance that a cheat is being entered.
   */
  private cheatBuffer = '';

  private pushCheatKey(key: string): void {
    if (key.length !== 1 || !/[a-z]/i.test(key)) return;
    // Bounded so a long session cannot grow it; longer than any code, so tails still match.
    this.cheatBuffer = (this.cheatBuffer + key.toLowerCase()).slice(-12);

    if (this.cheatBuffer.endsWith('iddqd')) {
      this.cheatBuffer = '';
      // Nothing to open over a screen that is already up.
      if (!this.screens.isShowing) this.openShiftSkip();
      return;
    }
    if (this.cheatBuffer.endsWith('idkfa')) {
      this.cheatBuffer = '';
      if (this.screens.isShowing) return;
      this.refill();
      /*
       * Said out loud on the radio log. A cheat that changes numbers on a card the player
       * may not be looking at is indistinguishable from one that did nothing.
       */
      this.sync.messages.push({
        speaker: 'RIG CONTROL',
        text: 'Magazine, tubes and rack full. Hull sound.',
        at: this.sim.time,
      });
    }
  }

  /** Top everything up — for weapon-feel iteration without hunting for ammunition. */
  refill(): void {
    const r = this.sim.resources;
    r.ammoReserve = 6000;
    r.torpedoes = 8;
    r.depthCharges = 12;
    r.integrity = 100;
    r.crew = r.crewCapacity;
    this.sim.rig.magazine = 1800;
    this.sim.rig.reloadTimer = 0;
  }

  /** Pick the contact nearest a screen point — used by tap-to-select on the water. */
  pickContactAt(screenX: number, screenY: number): EntityId | null {
    let best: EntityId | null = null;
    let bestDist = 60;
    const engine = this.world.engine;
    const camera = this.world.camera;
    const viewProj = camera.getViewMatrix().multiply(camera.getProjectionMatrix());

    for (const v of this.sim.vessels) {
      if (v.detection === 'undetected' || v.state === 'dead') continue;
      const p = Vector3.Project(
        new Vector3(v.pos.x, v.pos.y, v.pos.z),
        Matrix.Identity(),
        viewProj,
        new Viewport(0, 0, engine.getRenderWidth(), engine.getRenderHeight()),
      );
      const d = Math.hypot(p.x - screenX, p.y - screenY);
      if (d < bestDist) { bestDist = d; best = v.id; }
    }
    return best;
  }

  dispose(): void {
    this.sync.dispose();
    this.tutorial.dispose();
    this.look.dispose();
    this.hud.dispose();
    this.screens.dispose();
    this.audio.dispose();
    this.world.dispose();
  }
}
